import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core'
import { fromEvent } from 'rxjs/observable/fromEvent'
import { merge } from 'rxjs/observable/merge'
import { timer } from 'rxjs/observable/timer'
import { distinctUntilChanged, filter, map, mapTo, switchMapTo, takeUntil, tap } from 'rxjs/operators'
import { Subject } from 'rxjs/Subject'

import { environment } from '../../../environments/environment'
import { CommonComponent } from '../common.component'
import { KeyCode } from '../utils/keycode'
import { TypingStatus } from './typing-status'

@Component({
  selector: 'app-textbox',
  templateUrl: './textbox.component.html',
  styleUrls: ['./textbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextboxComponent extends CommonComponent implements AfterViewInit {
  cancelTyping$ = new Subject()
  @Input() className: string
  @Input() text: string
  @Input() disabled: boolean
  @Output() onMessageText = new EventEmitter<string>()
  @Output() onTyping = new EventEmitter<any>()
  @Output() onStopTyping = new EventEmitter<any>()
  @Output() onCouldNotPaste = new EventEmitter<any>()
  private typingInterval
  @ViewChild('message') private message: ElementRef

  constructor() {
    super()
  }

  get messageText() {
    return this.message.nativeElement.value
  }

  set messageText(msg: string) {
    this.message.nativeElement.value = msg
  }

  ngAfterViewInit(): void {
    fromEvent(this.message.nativeElement, 'keydown').pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((e: KeyboardEvent) => {
      if ((e.which === KeyCode.KEY_ENTER || e.which === KeyCode.KEY_RETURN) && !e.shiftKey) {
        e.preventDefault()
      }
      if (this.messageText.length >= environment.chat.charsLimit
        && e.which !== KeyCode.KEY_LEFT
        && e.which !== KeyCode.KEY_RIGHT
        && e.which !== KeyCode.KEY_DOWN
        && e.which !== KeyCode.KEY_PAGE_UP
        && e.which !== KeyCode.KEY_CLEAR
        && e.which !== KeyCode.KEY_BACK_SPACE
        && e.which !== KeyCode.KEY_DELETE) {
        e.preventDefault()
      }
    })

    const input$ = fromEvent(this.message.nativeElement, 'keyup').pipe(
      // need to filter keycodes which fires sending message
      filter((e: KeyboardEvent) =>
        e.which !== KeyCode.KEY_ENTER &&
        e.which !== KeyCode.KEY_RETURN
      ),
      map((e: any) => e.target.value))

    const stopTyping$ = merge(
      input$.pipe(switchMapTo(timer(environment.chat.stopTyping))),
      this.cancelTyping$
    )

    merge(
      input$.pipe(mapTo(TypingStatus.Typing)),
      stopTyping$.pipe(mapTo(TypingStatus.StopTyping))
    ).pipe(
      distinctUntilChanged(),
      takeUntil(this.ngUnsubscribe))
      .subscribe((status: TypingStatus) => {
        if (status === TypingStatus.Typing) {
          this.onTyping.emit()
          // start interval
          this.typingInterval = setInterval(() => this.onTyping.emit(), environment.chat.stillTyping)
        } else {
          this.onStopTyping.emit()
          // stop interval
          if (this.typingInterval) {
            clearInterval(this.typingInterval)
          }
        }
      })

    input$.pipe(
      takeUntil(this.ngUnsubscribe),
      distinctUntilChanged()
    ).subscribe(text => this.onMessageText.emit(text))
  }

  onPaste(event) {
    const clipboardText = event.clipboardData.getData('Text')
    if ((this.messageText.length + clipboardText.length) > environment.chat.charsLimit) {
      this.onCouldNotPaste.emit()
      event.preventDefault()
    }
  }

  enterTextArea() {
  }

  exitTextArea(e) {
    e.preventDefault()
    e.stopPropagation()

    this.cancelTyping$.next()
  }

  focus() {
    this.message.nativeElement.focus()
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/textbox/textbox.component.ts